// Chakra UI theme — dark-only, navy + gold, Cormorant Garamond + Inter
// Spec: docs/superpowers/specs/2026-05-03-blp-landing-redesign-design.md §2
//
// Many sections of the codebase still reference legacy size/variant tokens
// (`<Heading size="4xl">`, `<Button variant="primary">`, etc.). Those tokens
// are preserved here but their visual values are remapped to the new palette.

import { extendTheme } from '@chakra-ui/react';
import { brandColors, chakraColorScales, cssCustomProperties } from './colors';

const config = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
};

const fonts = {
  heading: "'Cormorant Garamond', 'Georgia', 'Times New Roman', serif",
  subheading: "'Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', sans-serif",
  body: "'Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', sans-serif",
  ui: "'Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Monaco', 'Consolas', monospace",
};

const components = {
  Button: {
    baseStyle: {
      fontFamily: 'ui',
      fontWeight: 600,
      borderRadius: '6px',
      letterSpacing: '-0.005em',
      transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
    },
    variants: {
      primary: {
        bg: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
        color: brandColors.navy[900],
        _hover: {
          color: brandColors.navy[900],
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 32px rgba(212,168,95,0.25)',
          bg: 'linear-gradient(135deg, #E0B873 0%, #D4A85F 100%)',
          textDecoration: 'none',
          _disabled: { transform: 'none' },
        },
        _active: {
          color: brandColors.navy[900],
          transform: 'translateY(0)',
          bg: 'linear-gradient(135deg, #C99846 0%, #D4A85F 100%)',
        },
        _loading: {
          opacity: 1,
          bg: 'linear-gradient(135deg, #C99846 0%, #D4A85F 100%)',
          color: brandColors.navy[900],
          _hover: {
            bg: 'linear-gradient(135deg, #C99846 0%, #D4A85F 100%)',
            transform: 'none',
            boxShadow: 'none',
          },
        },
        _disabled: {
          opacity: 0.75,
          bg: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
          color: brandColors.navy[900],
          cursor: 'not-allowed',
          _hover: {
            bg: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
      secondary: {
        bg: brandColors.navy[700],
        color: brandColors.text.primary,
        border: '1px solid',
        borderColor: brandColors.border.subtle,
        _hover: {
          color: brandColors.text.primary,
          bg: brandColors.navy[600],
          borderColor: brandColors.border.hover,
          transform: 'translateY(-1px)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
          textDecoration: 'none',
        },
        _active: { transform: 'translateY(0)' },
      },
      outline: {
        bg: 'transparent',
        color: brandColors.gold[500],
        border: '1px solid',
        borderColor: brandColors.gold[500],
        _hover: {
          bg: brandColors.gold[500],
          color: brandColors.navy[900],
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 32px rgba(212,168,95,0.20)',
        },
        _active: { transform: 'translateY(0)' },
      },
      ghost: {
        color: brandColors.text.secondary,
        _hover: {
          bg: 'rgba(255,255,255,0.04)',
          color: brandColors.gold[500],
        },
      },
      link: {
        color: brandColors.gold[500],
        fontWeight: 500,
        _hover: {
          textDecoration: 'none',
          color: brandColors.gold[400],
        },
      },
    },
    defaultProps: {
      variant: 'primary',
    },
  },

  Heading: {
    baseStyle: {
      fontFamily: 'heading',
      fontWeight: 500,
      lineHeight: 1.15,
      letterSpacing: '0',
      color: brandColors.text.primary,
    },
    sizes: {
      '4xl': {
        fontFamily: 'heading',
        fontSize: ['2.5rem', '3rem', '3.5rem', '4.5rem'],
        fontWeight: 500,
        letterSpacing: '0',
      },
      '3xl': {
        fontFamily: 'heading',
        fontSize: ['2rem', '2.5rem', '3rem'],
        fontWeight: 500,
        letterSpacing: '0',
      },
      '2xl': {
        fontFamily: 'heading',
        fontSize: ['1.75rem', '2rem', '2.5rem'],
        fontWeight: 500,
        letterSpacing: '0',
      },
      'xl': {
        fontFamily: 'heading',
        fontSize: ['1.5rem', '1.75rem', '2rem'],
        fontWeight: 500,
        letterSpacing: '0',
      },
      'lg': {
        fontFamily: 'heading',
        fontSize: ['1.25rem', '1.5rem', '1.75rem'],
        fontWeight: 500,
        letterSpacing: '0',
      },
      'md': {
        fontFamily: 'heading',
        fontSize: ['1.125rem', '1.25rem', '1.5rem'],
        fontWeight: 500,
        letterSpacing: '0',
      },
      'sm': {
        fontFamily: 'heading',
        fontSize: ['1rem', '1.125rem', '1.25rem'],
        fontWeight: 500,
      },
    },
  },

  Text: {
    baseStyle: {
      fontFamily: 'body',
      lineHeight: 1.7,
      letterSpacing: '-0.005em',
      color: brandColors.text.secondary,
    },
    variants: {
      hero: {
        fontSize: ['md', 'lg', 'xl'],
        fontWeight: 400,
        lineHeight: 1.6,
        color: brandColors.text.muted,
      },
      subtitle: {
        fontSize: ['md', 'lg'],
        fontWeight: 500,
        lineHeight: 1.6,
        color: brandColors.text.muted,
      },
      body: {
        fontSize: ['sm', 'md'],
        fontWeight: 400,
        lineHeight: 1.7,
        color: brandColors.text.secondary,
      },
      caption: {
        fontSize: ['xs', 'sm'],
        fontWeight: 400,
        lineHeight: 1.5,
        color: brandColors.text.muted,
      },
      mono: {
        fontFamily: 'mono',
        fontSize: ['xs', 'sm'],
        fontWeight: 500,
        letterSpacing: '0.025em',
        color: brandColors.text.primary,
      },
    },
  },

  Card: {
    baseStyle: {
      container: {
        bg: brandColors.navy[700],
        borderColor: brandColors.border.subtle,
        borderWidth: '1px',
        borderRadius: '16px',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        _hover: {
          transform: 'translateY(-2px)',
          borderColor: brandColors.border.hover,
          boxShadow: '0 16px 40px rgba(0,0,0,0.40)',
        },
      },
    },
  },

  Link: {
    baseStyle: {
      fontFamily: 'ui',
      color: brandColors.text.secondary,
      fontWeight: 400,
      transition: 'color 0.2s ease',
      _hover: {
        textDecoration: 'none',
        color: brandColors.gold[500],
      },
    },
  },

  Badge: {
    baseStyle: {
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: 600,
      fontSize: '0.6875rem',
      borderRadius: '6px',
      px: 2.5,
      py: 1,
    },
    variants: {
      gold: {
        bg: 'linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)',
        color: brandColors.navy[900],
      },
      subtle: {
        bg: 'rgba(212,168,95,0.12)',
        color: brandColors.gold[500],
      },
    },
  },

  Input: {
    baseStyle: {
      field: {
        color: brandColors.text.primary,
      },
    },
    variants: {
      outline: {
        field: {
          bg: 'transparent',
          borderColor: brandColors.border.subtle,
          color: brandColors.text.primary,
          _placeholder: { color: brandColors.text.muted },
          _hover: { borderColor: brandColors.border.hover },
          _focus: {
            borderColor: brandColors.gold[500],
            boxShadow: '0 0 0 1px ' + brandColors.gold[500],
          },
        },
      },
    },
  },
};

const globalStyles = {
  global: () => ({
    ':root': cssCustomProperties,
    'html, body': {
      bg: brandColors.navy[900],
      color: brandColors.text.primary,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    body: {
      bg: brandColors.navy[900],
      color: brandColors.text.primary,
    },
    a: {
      color: brandColors.text.secondary,
      _hover: {
        textDecoration: 'none',
        color: brandColors.gold[500],
      },
    },
    h1: { color: brandColors.text.primary },
    h2: { color: brandColors.text.primary },
    h3: { color: brandColors.text.primary },
    h4: { color: brandColors.text.primary },
    h5: { color: brandColors.text.primary },
    h6: { color: brandColors.text.primary },
    '*::selection': {
      bg: brandColors.gold[500],
      color: brandColors.navy[900],
    },
    '*::-webkit-scrollbar': { width: '10px' },
    '*::-webkit-scrollbar-track': { bg: brandColors.navy[900] },
    '*::-webkit-scrollbar-thumb': {
      bg: brandColors.gold[500],
      borderRadius: '5px',
    },
    '*::-webkit-scrollbar-thumb:hover': { bg: brandColors.gold[400] },
    '@media (prefers-reduced-motion: reduce)': {
      '*, *::before, *::after': {
        animationDuration: '0.01ms !important',
        animationIterationCount: '1 !important',
        transitionDuration: '0.01ms !important',
        scrollBehavior: 'auto !important',
      },
    },
  }),
};

const theme = extendTheme({
  config,
  colors: chakraColorScales,
  fonts,
  components,
  styles: globalStyles,

  shadows: {
    sm: '0 2px 8px rgba(0,0,0,0.20)',
    md: '0 4px 12px rgba(0,0,0,0.25)',
    lg: '0 8px 24px rgba(0,0,0,0.30)',
    xl: '0 12px 32px rgba(0,0,0,0.35)',
    'dark-sm': '0 2px 8px rgba(212,168,95,0.10)',
    'dark-md': '0 4px 12px rgba(212,168,95,0.15)',
    'dark-lg': '0 8px 20px rgba(212,168,95,0.20)',
    'dark-xl': '0 12px 28px rgba(212,168,95,0.25)',
    'gold-sm': '0 4px 12px rgba(212,168,95,0.15)',
    'gold-md': '0 8px 24px rgba(212,168,95,0.20)',
    'gold-lg': '0 12px 32px rgba(212,168,95,0.25)',
    'card': '0 8px 24px rgba(0,0,0,0.30)',
    'card-hover': '0 16px 40px rgba(0,0,0,0.40)',
  },

  radii: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
  },

  space: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2.5rem',
    xl: '5rem',
    xxl: '8rem',
    xxxl: '10rem',
  },

  sizes: {
    container: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
    },
  },
});

export default theme;
